import { ResponsiveModal } from "@/components/ResponsiveModal/ResponsiveModal";
import { Tables } from "database.types";
import { useBinders } from "@/hooks/useBinders";
import { useCallback, useEffect, useMemo, useState } from "react";
import useVariantsCards from "@/hooks/useVariantsCards";
import { sortVariantsByConditions } from "@/utils/sortVariantsByConditions";
import { useGradingCompanies } from "@/hooks/useGradingCompanies";
import { sortGraders } from "@/utils/sortGraders";
import { useGradesQuery } from "@/queries/useGradesQuery";
import { sortGrades } from "@/utils/sortGrades";
import { Label } from "@radix-ui/react-label";
import { Input } from "@/components/ui/input";
import { usePriceInput } from "@/utils/usePriceInput";
import { formatPrice } from "@/utils/formatPrice";
import { Button } from "@/components/ui/button";
import { useGradedPricesStore } from "@/stores/useGradedPricesStore";
import { Spinner } from "@/components/ui/spinner";
import { AddCardToBinderBindersInput } from "../AddCardToBinderBindersInput/AddCardToBinderBindersInput";
import { AddCardToBinderOptions } from "../AddCardToBinderOptions/AddCardToBinderOptions";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { updateVariantInBinder } from "@/api/binders";
import { BinderVariant, UpdateVariantInBinderOptions } from "@/types/binders";
import { X } from "lucide-react";
import { AddCardToBinderTotalPrice } from "../AddCardToBinderTotalPrice/AddCardToBinderTotalPrice";
import { EditCardInBinderDialogSkeleton } from "./EditCardInBinderDialogSkeleton";
import { invalidateBinderWatchlistVariantsQueries } from "@/queries/common";

interface Props {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  binderItem: BinderVariant;
}

export function EditCardInBinderDialog({
  binderItem,
  open,
  onOpenChange,
}: Props) {
  const { load: loadGradedPrice, prices: gradedPrices } =
    useGradedPricesStore();

  const cardId = binderItem.variant.card_id;

  const [isShowAddedAnimation, setIsShowAddedAnimation] = useState(false);

  const [wasSuccess, setWasSuccess] = useState(false);

  const [validationErrorMessage, setValidationErrorErrorMessage] = useState<
    string | null
  >(null);

  const queryClient = useQueryClient();

  const { mutateAsync, isPending } = useMutation({
    mutationFn: async (options: UpdateVariantInBinderOptions) => {
      return updateVariantInBinder(options);
    },
    onSuccess() {
      onSuccess();
      invalidateBinderWatchlistVariantsQueries();
    },
  });

  const { data: binders = [], isLoading: isLoadingBinders } = useBinders();

  const { data: variants = [], isLoading: isLoadingVariants } =
    useVariantsCards({
      cardId,
      enabled: open,
    });

  const { data: gradingCompanies, isLoading: isLoadingGradingCompanies } =
    useGradingCompanies();

  const { data: grades, isLoading: isLoadingGrades } = useGradesQuery();

  const [selectedVariantId, setSelectedVariantId] = useState<
    Tables<"variants">["id"] | null
  >(null);

  const [purchasePriceInput, setPurchasePriceInput] = usePriceInput("", {
    min: 0.01,
  });

  const [selectedBinderId, setSelectedBinderId] = useState<
    Tables<"binders">["id"] | null
  >(null);

  const [selectedGradingCompanyId, setSelectedGradingCompanyId] = useState<
    Tables<"grading_companies">["id"] | null
  >(null);

  const [selectedGradeId, setSelectedGradeId] = useState<
    Tables<"grades">["id"] | null
  >(null);

  const selectedVariant = useMemo(() => {
    return variants.find((variant) => variant.id === selectedVariantId);
  }, [selectedVariantId, variants]);

  const gradingCompanyOptions = useMemo(() => {
    const sortedGradingCompanies = sortGraders(gradingCompanies?.data || []);

    return sortedGradingCompanies.map((gradingCompany) => ({
      id: gradingCompany.id,
      label: gradingCompany.name,
    }));
  }, [gradingCompanies]);

  const variantOptions = useMemo(() => {
    const sortedVariants = sortVariantsByConditions(variants);

    return sortedVariants.map((variant) => {
      const name = variant.condition.name;
      const price = variant.price;
      const formattedPrice = price ? formatPrice(price) : "";

      const label = formattedPrice ? `${name} (${formattedPrice})` : name;

      return {
        id: variant.id,
        label,
      };
    });
  }, [variants]);

  const gradeOptions = useMemo(() => {
    const sortedGrades = sortGrades(grades || []);

    if (!sortedGrades) {
      return [];
    }

    return sortedGrades.map((grade) => ({
      id: grade.id,
      label: grade.name,
    }));
  }, [grades]);

  const sortedBinders = useMemo(() => {
    return [...binders].sort((a, b) => {
      return Number(b.card_count) - Number(a.card_count);
    });
  }, [binders]);

  useEffect(() => {
    if (variantOptions.length > 0 && !selectedVariantId) {
      setSelectedVariantId(variantOptions[0].id);
    }

    if (sortedBinders.length > 0 && !selectedBinderId) {
      setSelectedBinderId(sortedBinders[0].id);
    }
  }, [selectedBinderId, selectedVariantId, sortedBinders, variantOptions]);

  useEffect(() => {
    if (selectedGradeId && selectedGradingCompanyId) {
      loadGradedPrice(cardId, selectedGradingCompanyId, selectedGradeId);
    }
  }, [selectedGradeId, selectedGradingCompanyId, cardId, loadGradedPrice]);

  const isLoading =
    isLoadingBinders ||
    isLoadingVariants ||
    isLoadingGradingCompanies ||
    isLoadingGrades;

  const isDisabled = isPending || isShowAddedAnimation;

  const onSuccess = useCallback(() => {
    setWasSuccess(true);
    setIsShowAddedAnimation(true);
    setTimeout(() => {
      setIsShowAddedAnimation(false);
    }, 2500);
    onOpenChange(false);
  }, [onOpenChange]);

  useEffect(() => {
    if (binderItem.grading_company_id) {
      setSelectedGradingCompanyId(binderItem.grading_company_id);
    }
    if (binderItem.grade_id) {
      setSelectedGradeId(binderItem.grade_id.toString());
    }

    if (binderItem.variant_id) {
      setSelectedVariantId(binderItem.variant_id);
    }

    if (binderItem.purchase_price) {
      setPurchasePriceInput(binderItem.purchase_price.toString());
    }

    if (binderItem.binder_id) {
      setSelectedBinderId(binderItem.binder_id);
    }
  }, [
    binderItem.grading_company_id,
    binderItem.grade_id,
    binderItem.variant_id,
    binderItem.purchase_price,
    setPurchasePriceInput,
    binderItem.binder_id,
  ]);

  const onSubmit = useCallback(async () => {
    setValidationErrorErrorMessage(null);

    if (!selectedBinderId || !selectedVariantId) {
      return;
    }

    if (selectedGradeId && !selectedGradingCompanyId) {
      setValidationErrorErrorMessage("Please select a grader");
      return;
    }

    if (selectedGradingCompanyId && !selectedGradeId) {
      setValidationErrorErrorMessage("Please select a grade");
      return;
    }

    const data = {
      binderId: selectedBinderId,
      variantId: selectedVariantId,
      purchasePrice: purchasePriceInput ? Number(purchasePriceInput) : null,
      gradingCompanyId: selectedGradingCompanyId,
      gradeId: selectedGradeId,
      scanned: false,
      relationId: binderItem.id,
    };

    await mutateAsync(data);
  }, [
    mutateAsync,
    purchasePriceInput,
    selectedBinderId,
    selectedGradeId,
    selectedGradingCompanyId,
    selectedVariantId,
    binderItem.id,
  ]);

  const onClose = useCallback(() => {
    onOpenChange(false);
  }, [onOpenChange]);

  if (isLoading) {
    return (
      <ResponsiveModal
        open={open}
        onOpenChange={onOpenChange}
        title="Add to Binder"
      >
        <EditCardInBinderDialogSkeleton />
      </ResponsiveModal>
    );
  }

  return (
    <ResponsiveModal
      open={open}
      onOpenChange={onOpenChange}
      title="Add to Binder"
      repositionInputs={false}
    >
      <div className="grid gap-4">
        <AddCardToBinderOptions
          options={variantOptions}
          value={selectedVariantId}
          onChange={setSelectedVariantId}
          label="Variant"
          cancelable={false}
          disabled={isDisabled}
        />

        <AddCardToBinderOptions
          options={gradingCompanyOptions}
          value={selectedGradingCompanyId}
          onChange={setSelectedGradingCompanyId}
          label="Grader"
          cancelable
          disabled={isDisabled}
        />

        <AddCardToBinderOptions
          options={gradeOptions}
          value={selectedGradeId}
          onChange={setSelectedGradeId}
          label="Grade"
          cancelable
          disabled={isDisabled}
        />
        <div className="grid gap-2">
          <Label className="font-medium">Purchase Price ($, optional)</Label>
          <Input
            placeholder=""
            value={purchasePriceInput}
            onChange={(e) => setPurchasePriceInput(e.target.value)}
            disabled={isDisabled}
          />
        </div>
        {cardId && selectedVariant && (
          <AddCardToBinderTotalPrice
            cardId={cardId}
            selectedVariant={selectedVariant}
            gradingCompanyId={selectedGradingCompanyId}
            gradeId={selectedGradeId}
            quantity={1}
          />
        )}
        <div className="grid gap-2">
          <Label className="font-medium">Binder</Label>
          <AddCardToBinderBindersInput
            options={sortedBinders}
            value={selectedBinderId}
            onChange={setSelectedBinderId}
            disabled={isDisabled}
          />
        </div>

        {validationErrorMessage && (
          <div className="text-red-500">{validationErrorMessage}</div>
        )}

        {!isShowAddedAnimation && (
          <Button className="w-full" disabled={isDisabled} onClick={onSubmit}>
            {isPending && <Spinner className="w-4 h-4 text-white" />}
            Update
          </Button>
        )}

        {isShowAddedAnimation && (
          <Button className="w-full cursor-default" variant="success">
            Updated
          </Button>
        )}

        {wasSuccess && (
          <Button
            className="w-full cursor-default"
            variant="ghost"
            onClick={onClose}
          >
            <X className="w-4 h-4" />
            Close
          </Button>
        )}
      </div>
    </ResponsiveModal>
  );
}
